import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Calendar, ArrowRightLeft, CreditCard, User, LogIn, LogOut, XCircle, Printer, Plus, Layers, ShieldCheck, FileText, Download, Repeat } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import Portal from "./Portal";
import BookingRecoveryMenu from "./BookingRecoveryMenu";
import CRSStatusBadge from "./CRSStatusBadge";

const INR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const fmtDate = (s) => s ? new Date(s + (s.length === 10 ? "T00:00:00" : "")).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const TabBtn = ({ active, onClick, icon: Icon, label, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg transition-all whitespace-nowrap min-h-[40px] shrink-0 ${
      active ? "bg-[#4F46E5] text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
    }`}
  >
    <Icon size={14} /> {label}
  </button>
);

const Field = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">{label}</p>
    <p className="text-sm font-medium text-slate-900 mt-0.5">{value ?? "—"}</p>
  </div>
);

// Batch-2 (post-Item 3): render the uploaded ID photo(s) inline on the
// Booking Detail dialog so front-desk staff can visually verify without
// leaving the panel. Blob-fetched with the same JWT the rest of the app uses
// so <img src> works even though the storage endpoint is auth-gated.
function IdThumbnails({ booking, onOpen }) {
  const paths = (booking.id_photos && booking.id_photos.length)
    ? booking.id_photos
    : (booking.id_photo_url ? [booking.id_photo_url] : []);
  const [urls, setUrls] = useState({});
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    const controllers = [];
    (async () => {
      const next = {};
      for (const p of paths) {
        try {
          const ctl = new AbortController(); controllers.push(ctl);
          const r = await api.get("/pms/uploads/download", {
            params: { path: p }, responseType: "blob", signal: ctl.signal,
          });
          next[p] = URL.createObjectURL(r.data);
        } catch (_) {
          next[p] = null;  // still show as a "file · download" tile
        }
      }
      setUrls(next);
    })();
    return () => {
      controllers.forEach((c) => c.abort());
      Object.values(urls).forEach((u) => u && URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.id, booking.id_photo_url, JSON.stringify(booking.id_photos || [])]);

  const verified = !!(booking.id_type && booking.id_number && paths.length > 0);
  return (
    <div data-testid="id-thumbnails" className="border-t border-slate-100 pt-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-400 flex items-center gap-1.5">
          <ShieldCheck size={12} className={verified ? "text-emerald-600" : "text-slate-400"} />
          ID Verification
          {verified ? (
            <span className="ml-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-[1px]">Verified</span>
          ) : (
            <span className="ml-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-[1px]">Pending</span>
          )}
        </p>
        {onOpen && (
          <button data-testid="id-thumbs-manage" onClick={onOpen}
            className="text-[11px] font-semibold text-[#4F46E5] hover:text-[#4338CA]">
            {paths.length ? "Manage / add more" : "Upload ID(s)"}
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="ID type" value={booking.id_type || "—"} />
        <Field label="ID number" value={booking.id_number ? <span className="font-mono">{booking.id_number}</span> : "—"} />
        <Field label="Nationality" value={booking.nationality || "—"} />
        <Field label="Photos uploaded" value={<b>{paths.length} / 4</b>} />
      </div>
      {paths.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-3">
          {paths.map((p, i) => {
            const url = urls[p];
            const isPdf = /\.pdf(\?|$)/i.test(p);
            return (
              <div key={p + i} data-testid={`id-thumb-${i}`} className="relative w-28 h-28 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden group">
                {isPdf ? (
                  <a href={url || "#"} target="_blank" rel="noreferrer"
                    className="w-full h-full flex flex-col items-center justify-center text-slate-500 hover:bg-slate-100">
                    <FileText size={28} className="text-[#4F46E5]" />
                    <span className="text-[10px] mt-1 font-semibold">PDF · Open</span>
                  </a>
                ) : url ? (
                  <button onClick={() => setPreview({ url, name: p.split("/").pop() })} className="block w-full h-full">
                    <img src={url} alt={`ID ${i + 1}`} className="w-full h-full object-cover group-hover:opacity-80 transition-opacity" />
                  </button>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] text-slate-400">
                    <Loader2 className="animate-spin" size={16} />
                  </div>
                )}
                <span className="absolute top-1 left-1 text-[9px] font-bold bg-slate-900/70 text-white rounded px-1.5 py-0.5">#{i + 1}</span>
                {url && !isPdf && (
                  <a href={url} download={`id-${i + 1}-${(booking.guest || "guest").replace(/\s+/g, "_")}`}
                    className="absolute top-1 right-1 text-white bg-slate-900/60 rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity" title="Download">
                    <Download size={11} />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-3 text-[11px] text-slate-500 italic">No ID photos on file yet. Click <b>Upload ID(s)</b> above to attach up to 4.</div>
      )}
      {preview && (
        <div data-testid="id-preview-lightbox" onClick={() => setPreview(null)}
          className="fixed inset-0 bg-slate-900/80 z-[70] flex items-center justify-center p-6 cursor-zoom-out">
          <img src={preview.url} alt={preview.name} className="max-w-full max-h-full rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

export default function BookingDetailDialog({ bookingId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("details");
  const [busy, setBusy] = useState(false);
  const [guest, setGuest] = useState({});
  const [dates, setDates] = useState({ checkin: "", checkout: "" });
  const [moveUnit, setMoveUnit] = useState("");
  const [movable, setMovable] = useState({ loading: false, units: [], current_unit_id: "" });
  const [exchange, setExchange] = useState({ loading: false, candidates: [], target_id: "" });
  const [pay, setPay] = useState({ amount: "", mode: "UPI", remark: "" });
  const [addUnit, setAddUnit] = useState({ open: false, unit_ids: [], available: [], checkin: "", checkout: "", rate: "", adults: 1, children: 0, rate_inclusive: true, tax_rate: 0, loadingAvail: false });
  const [idVerify, setIdVerify] = useState({ open: false, id_type: "", id_number: "", id_photo_url: "", id_photos: [] });
  const [hkPrompt, setHkPrompt] = useState(false);

  const openAddUnit = () => {
    // Item 8 (Batch 6): seed dates + tax from parent (established taxation
    // logic per #21 — never a hardcoded default). Available units are
    // fetched from the server, which reuses `_check_availability` so the
    // #29 blocking-group rule is applied consistently.
    setAddUnit((s) => ({
      ...s, open: true, unit_ids: [], available: [], rate: "", adults: 1, children: 0,
      checkin: b?.checkin || "", checkout: b?.checkout || "",
      rate_inclusive: !!b?.rate_inclusive,
      tax_rate: typeof b?.tax_rate === "number" ? b.tax_rate : 0,
    }));
  };

  // Re-fetch the availability list whenever dates change (or the dialog opens).
  useEffect(() => {
    const gid = data?.group?.id;
    if (!addUnit.open || !addUnit.checkin || !addUnit.checkout || !gid) return;
    let alive = true;
    setAddUnit((s) => ({ ...s, loadingAvail: true }));
    api.get(`/pms/reservations/group/${gid}/available-units`, {
      params: { checkin: addUnit.checkin, checkout: addUnit.checkout },
    }).then(({ data: d }) => {
      if (!alive) return;
      setAddUnit((s) => ({
        ...s,
        available: d.units || [],
        unit_ids: (s.unit_ids || []).filter((u) => (d.units || []).some((x) => x.id === u)),
        loadingAvail: false,
      }));
    }).catch(() => setAddUnit((s) => ({ ...s, loadingAvail: false, available: [] })));
    return () => { alive = false; };
  }, [addUnit.open, addUnit.checkin, addUnit.checkout, data?.group?.id]);

  const toggleAddUnitId = (uid) => {
    setAddUnit((s) => ({
      ...s,
      unit_ids: s.unit_ids.includes(uid) ? s.unit_ids.filter((x) => x !== uid) : [...s.unit_ids, uid],
    }));
  };

  const submitAddUnit = async () => {
    if (!addUnit.unit_ids.length) return toast.error("Pick at least one unit");
    const rate = parseFloat(addUnit.rate);
    if (!rate || rate <= 0) return toast.error("Enter a valid rate");
    setBusy(true);
    try {
      const eff = Number(addUnit.tax_rate) || 0;
      const netRate = addUnit.rate_inclusive && eff > 0
        ? Math.round((rate / (1 + eff / 100)) * 100) / 100
        : rate;
      const r = await api.post(`/pms/reservations/group/${groupId}/add-unit`, {
        unit_ids: addUnit.unit_ids,
        checkin: addUnit.checkin, checkout: addUnit.checkout,
        rate: netRate, adults: addUnit.adults, children: addUnit.children,
        tax_rate: eff, tax_exempt: false, rate_inclusive: addUnit.rate_inclusive,
      });
      toast.success(`${r.data.count} unit(s) added — consolidated invoice updated.`);
      setAddUnit({ ...addUnit, open: false });
      onChanged?.();
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  // ── Mandatory ID + Housekeeping-clean at check-in ─────────────────────────
  // Compliance rule: check-in must not complete without ID type + number + photo.
  // After a successful check-in we prompt the operator to mark the unit Clean —
  // one tap keeps Housekeeping status in sync with front-desk reality.
  const submitIdVerify = async () => {
    const photos = (idVerify.id_photos || []).filter(Boolean);
    // Backward compat: also send legacy id_photo_url = first photo. Batch-2
    // Item 3: up to 4 IDs per booking, mandatory at check-in.
    const primary = photos[0] || idVerify.id_photo_url.trim();
    if (!idVerify.id_type.trim() || !idVerify.id_number.trim() || !primary) {
      return toast.error("All three fields are mandatory — ID type, number and at least one photo.");
    }
    if (photos.length > 4) return toast.error("Maximum 4 ID uploads per check-in.");
    setBusy(true);
    try {
      await api.patch(`/pms/bookings/${b.id}/guest`, {
        id_type: idVerify.id_type.trim(),
        id_number: idVerify.id_number.trim(),
        id_photo_url: primary,
        id_photos: photos.length ? photos : [primary],
      });
      await api.post(`/pms/bookings/${b.id}/status`, { status: "checked_in", reason: "ID verified at check-in" });
      toast.success(`Guest checked in · ${photos.length || 1} ID(s) verified`);
      setIdVerify({ ...idVerify, open: false });
      setHkPrompt(true);
      onChanged?.();
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const markUnitClean = async () => {
    if (!data.property?.id || !data.unit?.id) { setHkPrompt(false); return; }
    setBusy(true);
    try {
      await api.post(`/pms/properties/${data.property.id}/housekeeping/${data.unit.id}/status`,
        { status: "clean", notes: `Auto-cleaned at ${b.guest}'s check-in` });
      toast.success(`Unit ${data.unit.name} marked Clean`);
    } catch (e) { toast.error(errMsg(e)); }
    setHkPrompt(false);
    setBusy(false);
  };

  const load = () => {
    setData(null);
    api.get(`/pms/bookings/${bookingId}`)
      .then(({ data: d }) => {
        setData(d);
        setGuest({ name: d.booking.guest || "", phone: d.booking.phone || "", email: d.booking.email || "" });
        setDates({ checkin: d.booking.checkin, checkout: d.booking.checkout });
        setMoveUnit(d.unit?.id || "");
      })
      .catch((e) => { toast.error(errMsg(e)); onClose(); });
  };
  useEffect(load, [bookingId]);

  const saveGuest = async () => {
    setBusy(true);
    try {
      await api.patch(`/pms/bookings/${bookingId}/guest`, guest);
      toast.success("Guest details updated");
      onChanged?.();
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const saveDates = async () => {
    setBusy(true);
    try {
      await api.post(`/pms/bookings/${bookingId}/move`, {
        unit_id: data.unit.id, checkin: dates.checkin, checkout: dates.checkout,
      });
      toast.success("Dates updated");
      onChanged?.();
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const saveMove = async () => {
    if (!moveUnit || moveUnit === data.unit?.id) return toast.error("Pick a different unit to move to");
    setBusy(true);
    try {
      await api.post(`/pms/bookings/${bookingId}/move`, { unit_id: moveUnit });
      toast.success("Booking moved");
      onChanged?.();
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  // Item 9 (Batch 6): fetch server-filtered movable units when the Move tab
  // opens. Reuses `_check_availability` on the backend so blocked/occupied
  // rooms never appear in the dropdown.
  useEffect(() => {
    if (tab !== "move" || !data?.booking?.id) return;
    let alive = true;
    setMovable((s) => ({ ...s, loading: true }));
    api.get(`/pms/bookings/${data.booking.id}/movable-units`).then(({ data: d }) => {
      if (!alive) return;
      setMovable({ loading: false, units: d.units || [], current_unit_id: d.current_unit_id });
      setMoveUnit((prev) => (d.units || []).some((u) => u.id === prev) ? prev : (d.units[0]?.id || ""));
    }).catch(() => setMovable({ loading: false, units: [], current_unit_id: "" }));
    return () => { alive = false; };
  }, [tab, data?.booking?.id]);

  // Item 10 (Batch 6): fetch exchange candidates on the same property.
  useEffect(() => {
    if (tab !== "exchange" || !data?.booking?.id) return;
    let alive = true;
    setExchange((s) => ({ ...s, loading: true }));
    api.get(`/pms/bookings/${data.booking.id}/exchangeable`).then(({ data: d }) => {
      if (!alive) return;
      setExchange({ loading: false, candidates: d.candidates || [], target_id: "" });
    }).catch(() => setExchange({ loading: false, candidates: [], target_id: "" }));
    return () => { alive = false; };
  }, [tab, data?.booking?.id]);

  const saveExchange = async () => {
    if (!exchange.target_id) return toast.error("Pick a booking to exchange with");
    const target = exchange.candidates.find((c) => c.id === exchange.target_id);
    if (!window.confirm(`Swap units between\n\n• ${data.booking.guest} · ${data.unit?.name}\n• ${target?.guest} · ${target?.unit_name}\n\nDates & rates stay put — only the room assignments swap. Continue?`)) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/pms/bookings/${bookingId}/exchange`, { target_booking_id: exchange.target_id });
      toast.success(`Swapped: ${r.a.guest} → ${r.a.new_unit_name} · ${r.b.guest} → ${r.b.new_unit_name}`);
      onChanged?.();
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const changeStatus = async (status, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await api.post(`/pms/bookings/${bookingId}/status`, { status, reason: "" });
      toast.success(`Booking ${status.replace("_", " ")}`);
      onChanged?.();
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const addPayment = async () => {
    const amt = parseFloat(pay.amount);
    if (!amt || amt <= 0) return toast.error("Enter a positive amount");
    setBusy(true);
    try {
      await api.post(`/pms/bookings/${bookingId}/payments`, { amount: amt, mode: pay.mode, remark: pay.remark });
      toast.success(`${INR(amt)} recorded`);
      setPay({ amount: "", mode: "UPI", remark: "" });
      onChanged?.();
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const [docKind, setDocKind] = useState("invoice"); // "invoice" | "voucher"
  const printInvoice = () => window.print();

  // DD/MM/YYYY per Atiti sample voucher
  const fmtDMY = (s) => {
    if (!s) return "—";
    const d = new Date(s + (String(s).length === 10 ? "T00:00:00" : ""));
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  };
  const fmtTime12 = (t) => {
    if (!t) return "";
    const [h, m] = String(t).split(":").map(Number);
    const hh = ((h + 11) % 12) + 1;
    return `${String(hh).padStart(2, "0")}:${String(m || 0).padStart(2, "0")}:00 ${h >= 12 ? "PM" : "AM"}`;
  };

  if (!data) {
    return (
      <div data-testid="booking-dialog-loading" className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl p-8"><Loader2 className="animate-spin text-slate-400" size={26} /></div>
      </div>
    );
  }

  const b = data.booking;
  // ── Group reservation aggregation ─────────────────────────
  // If this booking is part of a consolidated group (Quick Reservation with
  // multiple units), pull sibling bookings from the server response and
  // aggregate totals for a single Invoice / Voucher.
  const groupBookings = (data.group && data.group.bookings && data.group.bookings.length > 0)
    ? data.group.bookings
    : [{ ...b, unit_id: data.unit?.id, unit_name: data.unit?.name }];
  const isGrouped = groupBookings.length > 1;
  const groupId = data.group?.id || "";
  const perUnitInvoice = groupBookings.map((gb) => {
    const grand = Number(gb.amount || 0);
    const taxRate = (typeof gb.tax_rate === "number" && !gb.tax_exempt) ? gb.tax_rate : (gb.tax_exempt ? 0 : 5);
    const gstRate = taxRate / 100;
    const taxable = (typeof gb.subtotal === "number") ? gb.subtotal : (gstRate > 0 ? grand / (1 + gstRate) : grand);
    const tax = (typeof gb.tax_amount === "number") ? gb.tax_amount : Math.max(0, grand - taxable);
    const paidLine = Number(gb.paid || 0);
    const gnights = Number(gb.nights || 0) || Math.max(1, Math.round((new Date(gb.checkout) - new Date(gb.checkin)) / 86400000));
    return { gb, grand, taxRate, taxable, tax, paidLine, gnights };
  });
  const groupTotals = perUnitInvoice.reduce((a, x) => ({
    grand: a.grand + x.grand, taxable: a.taxable + x.taxable, tax: a.tax + x.tax, paid: a.paid + x.paidLine,
  }), { grand: 0, taxable: 0, tax: 0, paid: 0 });
  const paid = isGrouped ? groupTotals.paid : (b.paid || 0);
  const balance = Math.max(0, (isGrouped ? groupTotals.grand : (b.amount || 0)) - paid);
  const nights = b.nights || 0;
  const perNight = nights > 0 ? (b.amount / nights) : 0;

  return (
    <Portal>
    <div data-testid="booking-detail-dialog" className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[80] grid place-items-center p-3 sm:p-4 md:p-6 overflow-y-auto pt-safe pb-safe pl-safe pr-safe" onClick={onClose}>
      <div className="bg-white rounded-2xl w-[96vw] min-h-[92vh] max-h-[92vh] max-w-none shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white rounded-t-2xl border-b border-slate-100 px-6 py-4 flex items-center gap-4 z-10">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-400 flex items-center gap-2">
              Reservation · {b.channel}
              {isGrouped && (
                <span data-testid="group-badge" className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-[#4F46E5] bg-[#EEF2FF] border border-[#C7D2FE] rounded-full px-2 py-0.5">
                  <Layers size={10} /> Group · {groupBookings.length} units
                </span>
              )}
            </p>
            <h2 className="font-display text-xl font-bold text-slate-900 truncate">{b.guest || "Guest"}</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {isGrouped
                ? <>{groupBookings.length} units · {fmtDate(b.checkin)} → {fmtDate(b.checkout)} · Consolidated ₹{Number(groupTotals.grand).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</>
                : <>{data.unit?.name && <>Unit <b>{data.unit.name}</b> · </>}{fmtDate(b.checkin)} → {fmtDate(b.checkout)} · {nights} night{nights === 1 ? "" : "s"}</>}
            </p>
          </div>
          {isGrouped && (
            <button data-testid="add-unit-to-group" onClick={openAddUnit}
              className="flex items-center gap-1.5 text-[11px] font-semibold text-[#4F46E5] bg-[#EEF2FF] hover:bg-[#E0E7FF] border border-[#C7D2FE] rounded-full px-3 py-1.5">
              <Plus size={12} /> Add unit
            </button>
          )}
          {!isGrouped && data.group === null && b.reservation_group_id === undefined && null}
          {!isGrouped && groupId === "" && b.status !== "cancelled" && (
            <button data-testid="add-unit-single" onClick={async () => {
              // Bootstrap a group on this single booking (backfill parent), then open picker
              try {
                // Backend already returns group=null when no group_id; we generate a group_id
                // by upgrading this booking. Simplest path: assign a group_id on the fly
                // client-side, then call the same add-unit endpoint after ensuring the
                // parent booking has that group_id.
                // (Kept off for now to avoid surprising single-booking flows — user asked
                // Add booking only for multi-unit reservations.)
                toast.info("This is a single-unit reservation. Create it from Quick Reservation with multiple units to enable Add unit.");
              } catch (e) { toast.error(errMsg(e)); }
            }} className="hidden" />
          )}
          <BookingRecoveryMenu booking={b} onDone={() => { load?.(); }} />
          <CRSStatusBadge booking={b} onDone={() => { load?.(); }} />
          <span data-testid="booking-status-pill" className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${
            b.status === "cancelled" || b.status === "no_show" ? "bg-red-100 text-red-700" :
            b.status === "checked_in" ? "bg-emerald-100 text-emerald-700" :
            b.status === "checked_out" ? "bg-slate-100 text-slate-600" :
            "bg-indigo-100 text-indigo-700"
          }`}>{b.status || "upcoming"}</span>
          <button data-testid="booking-dialog-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        {/* Tabs — horizontal scroll on narrow viewports */}
        <div className="px-4 md:px-6 pt-3 md:pt-4 flex gap-2 overflow-x-auto border-b border-slate-100 -mx-0.5 scroll-hint-right md:scroll-hint-none">
          <TabBtn testId="tab-details" active={tab === "details"} onClick={() => setTab("details")} icon={User} label="Details" />
          <TabBtn testId="tab-dates" active={tab === "dates"} onClick={() => setTab("dates")} icon={Calendar} label="Extend / dates" />
          <TabBtn testId="tab-move" active={tab === "move"} onClick={() => setTab("move")} icon={ArrowRightLeft} label="Move" />
          <TabBtn testId="tab-exchange" active={tab === "exchange"} onClick={() => setTab("exchange")} icon={Repeat} label="Exchange" />
          <TabBtn testId="tab-invoice" active={tab === "invoice"} onClick={() => setTab("invoice")} icon={Printer} label="Invoice / Voucher" />
          <TabBtn testId="tab-payments" active={tab === "payments"} onClick={() => setTab("payments")} icon={CreditCard} label="Payments" />
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5 min-h-[280px] flex-1 overflow-y-auto">
          {tab === "details" && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Field label="Reservation" value={<span className="font-mono">{b.id?.slice(0, 8)}</span>} />
                <Field label="Channel" value={b.channel} />
                <Field label="Adults / children" value={`${b.adults || 1} / ${b.children || 0}`} />
                <Field label="Rate plan" value={b.rate_plan || "Base rate"} />
                <Field label="Amount" value={<b>{INR(b.amount)}</b>} />
                <Field label="Paid" value={INR(paid)} />
                <Field label="Balance" value={<span className={balance > 0 ? "text-amber-700 font-semibold" : "text-emerald-700 font-semibold"}>{INR(balance)}</span>} />
                <Field label="Per night" value={INR(perNight)} />
              </div>

              <div className="border-t border-slate-100 pt-4">
                <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-400 mb-3">Guest — edit inline</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input data-testid="edit-guest-name" value={guest.name} onChange={(e) => setGuest({ ...guest, name: e.target.value })} placeholder="Name" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                  <input data-testid="edit-guest-phone" value={guest.phone} onChange={(e) => setGuest({ ...guest, phone: e.target.value })} placeholder="Phone" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                  <input data-testid="edit-guest-email" value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} placeholder="Email" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <button data-testid="save-guest-btn" onClick={saveGuest} disabled={busy} className="mt-3 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-full px-5 py-2 disabled:opacity-50">
                  {busy ? <Loader2 className="animate-spin inline mr-2" size={13} /> : null} Save guest
                </button>
              </div>

              {b.status !== "cancelled" && b.status !== "no_show" && (
                <IdThumbnails booking={b} onOpen={() => setIdVerify({
                  open: true,
                  id_type: b.id_type || "Aadhaar",
                  id_number: b.id_number || "",
                  id_photo_url: b.id_photo_url || "",
                  id_photos: b.id_photos || [],
                })} />
              )}

              {b.status !== "cancelled" && b.status !== "no_show" && b.status !== "checked_out" && (
                <div className="border-t border-slate-100 pt-4 flex flex-wrap gap-2">
                  {b.status !== "checked_in" && (
                    <button data-testid="action-checkin" onClick={() => setIdVerify({
                      open: true,
                      id_type: b.id_type || "Aadhaar",
                      id_number: b.id_number || "",
                      id_photo_url: b.id_photo_url || "",
                      id_photos: b.id_photos || [],
                    })} disabled={busy}
                      className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-full px-4 py-2 disabled:opacity-50">
                      <LogIn size={14} /> Check in
                    </button>
                  )}
                  {b.status === "checked_in" && (
                    <button data-testid="action-checkout" onClick={() => changeStatus("checked_out")} disabled={busy}
                      className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold rounded-full px-4 py-2 disabled:opacity-50">
                      <LogOut size={14} /> Check out
                    </button>
                  )}
                  <button data-testid="action-cancel" onClick={() => changeStatus("cancelled", `Cancel booking for ${b.guest}?`)} disabled={busy}
                    className="flex items-center gap-2 bg-white hover:bg-red-50 border border-red-200 text-red-700 text-sm font-semibold rounded-full px-4 py-2 disabled:opacity-50">
                    <XCircle size={14} /> Cancel booking
                  </button>
                </div>
              )}
            </div>
          )}

          {tab === "dates" && (
            <div className="space-y-4">
              <p className="text-xs text-slate-500">Change check-in or check-out. The stay must not overlap another booking on this unit — the server verifies availability.</p>
              <div className="grid grid-cols-2 gap-3 max-w-md">
                <div>
                  <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">Check-in</label>
                  <input data-testid="edit-checkin" type="date" value={dates.checkin} onChange={(e) => setDates({ ...dates, checkin: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">Check-out</label>
                  <input data-testid="edit-checkout" type="date" value={dates.checkout} onChange={(e) => setDates({ ...dates, checkout: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <button data-testid="save-dates-btn" onClick={saveDates} disabled={busy || !dates.checkin || !dates.checkout || dates.checkout <= dates.checkin}
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-full px-5 py-2 disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin inline mr-2" size={13} /> : null} Save dates
              </button>
            </div>
          )}

          {tab === "move" && (
            <div className="space-y-4">
              <p className="text-xs text-slate-500">Move this booking to a different unit in the same property. Only units that are actually available for the booking's dates appear — blocked, stopsell and already-occupied rooms are hidden (server enforces the same availability rule that #29 uses).</p>
              {movable.loading ? (
                <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="animate-spin" size={13} /> Checking availability…</div>
              ) : movable.units.length === 0 ? (
                <div data-testid="move-none" className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
                  No other unit on this property is available for {data.booking.checkin} → {data.booking.checkout}. Try the <b>Exchange</b> tab to swap with another booking, or free up a room first.
                </div>
              ) : (
                <div className="max-w-md">
                  <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">Target unit ({movable.units.length} available)</label>
                  <select data-testid="edit-move-unit" value={moveUnit} onChange={(e) => setMoveUnit(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                    {movable.units.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <button data-testid="save-move-btn" onClick={saveMove} disabled={busy || !moveUnit || moveUnit === data.unit?.id || !movable.units.length}
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-full px-5 py-2 disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin inline mr-2" size={13} /> : null} Move booking
              </button>
            </div>
          )}

          {tab === "exchange" && (
            <div className="space-y-4" data-testid="exchange-panel">
              <p className="text-xs text-slate-500">
                Exchange (swap) unit assignments between two bookings on the same property.
                Each booking keeps its own dates, guest and rate — only the room swaps.
                Useful when two guests want to trade rooms without changing anything else.
              </p>
              {exchange.loading ? (
                <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="animate-spin" size={13} /> Loading bookings on this property…</div>
              ) : exchange.candidates.length === 0 ? (
                <div data-testid="exchange-none" className="text-xs bg-slate-50 border border-slate-200 text-slate-600 rounded-lg px-3 py-2">
                  No other active bookings on this property to exchange with.
                </div>
              ) : (
                <div className="max-w-lg space-y-2">
                  {exchange.candidates.map((c) => (
                    <label key={c.id} data-testid={`exchange-candidate-${c.id}`}
                      className={`flex items-center gap-3 border rounded-lg px-3 py-2.5 cursor-pointer transition-colors ${exchange.target_id === c.id ? "border-[#4F46E5] bg-indigo-50" : "border-slate-200 hover:border-slate-400"}`}>
                      <input type="radio" name="ex-target" value={c.id} checked={exchange.target_id === c.id}
              onChange={() => setExchange((s) => ({ ...s, target_id: c.id }))}
                        className="accent-[#4F46E5]" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">{c.guest} · {c.unit_name}</div>
                        <div className="text-[11px] text-slate-500">{c.checkin} → {c.checkout} · {c.channel || "Direct"} · <span className="uppercase">{c.status}</span></div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              <button data-testid="save-exchange-btn" onClick={saveExchange} disabled={busy || !exchange.target_id}
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-full px-5 py-2 disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin inline mr-2" size={13} /> : <Repeat size={13} className="inline mr-1.5" />} Swap units
              </button>
            </div>
          )}

          {tab === "invoice" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-xs text-slate-500 flex-1 min-w-0">
                  {docKind === "voucher"
                    ? "Guest-facing Reservation Voucher — pulls live from this property's General Settings and Policies."
                    : "Folio-style Tax Invoice bound to this property's General Settings and Taxation slabs."}
                </p>
                <div data-testid="doc-kind-toggle" className="inline-flex bg-slate-100 rounded-full p-1 text-[11px] font-semibold">
                  <button data-testid="doc-kind-invoice" onClick={() => setDocKind("invoice")}
                    className={`px-3 py-1.5 rounded-full transition-colors ${docKind === "invoice" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>Tax Invoice</button>
                  <button data-testid="doc-kind-voucher" onClick={() => setDocKind("voucher")}
                    className={`px-3 py-1.5 rounded-full transition-colors ${docKind === "voucher" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>Reservation Voucher</button>
                </div>
              </div>
              <div id="print-area" className="border border-slate-300 rounded-xl p-6 bg-white text-slate-900" style={{ fontFamily: "system-ui, sans-serif" }}>
                {docKind === "invoice" ? (<>
                {/* Header */}
                <div className="flex items-start justify-between border-b-2 border-slate-800 pb-4 mb-4">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {data.profile?.logo_url && <img src={data.profile.logo_url} alt="logo" className="h-14 w-14 object-contain rounded" />}
                    <div className="min-w-0">
                      <h3 className="font-bold text-lg leading-tight">{data.property?.name || "Property"}</h3>
                      <p className="text-[11px] text-slate-600 leading-tight mt-0.5">
                        {[data.profile?.address_1, data.profile?.address_2, data.profile?.city, data.profile?.state, data.profile?.postal_code, data.profile?.country].filter(Boolean).join(", ")}
                      </p>
                      <p className="text-[11px] text-slate-600 mt-0.5">
                        {data.profile?.phone && <>Ph: {data.profile.phone} · </>}
                        {data.profile?.email && <>{data.profile.email} · </>}
                        {data.profile?.website && <>{data.profile.website}</>}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <h4 className="font-bold text-base">{docKind === "voucher" ? "Reservation Voucher" : "Tax Invoice"}</h4>
                    {data.profile?.reg_number && <p className="text-[11px] text-slate-600 mt-1">GSTIN: <b>{data.profile.reg_number}</b></p>}
                    <p className="text-[11px] text-slate-500 mt-1">Ref · {b.id?.slice(0, 8).toUpperCase()}</p>
                    {docKind === "voucher" && <p className="text-[10px] text-emerald-700 font-semibold mt-1">Guest copy — not a tax document</p>}
                  </div>
                </div>

                {/* Guest / stay two-column block */}
                <div className="grid grid-cols-2 gap-6 mb-5 text-[12px]">
                  <div className="space-y-1">
                    <div><span className="text-slate-500 inline-block w-32">Folio No / Res No</span><b>{b.id?.slice(0, 8).toUpperCase()}</b></div>
                    <div><span className="text-slate-500 inline-block w-32">Guest Name</span><b>{b.guest || "—"}</b></div>
                    <div><span className="text-slate-500 inline-block w-32">Bill To</span><b>{b.company_name || b.guest || "—"}</b>{b.bill_to && b.bill_to !== "Guest" && <span className="text-[10px] text-slate-500 ml-2">({b.bill_to})</span>}</div>
                    {b.tax_id && <div><span className="text-slate-500 inline-block w-32">Tax ID / GSTIN</span><b className="font-mono uppercase">{b.tax_id}</b></div>}
                    <div><span className="text-slate-500 inline-block w-32">Address</span>{b.address || (data.profile?.country || "India")}</div>
                    <div><span className="text-slate-500 inline-block w-32">Phone / Email</span>{b.phone || "—"} · {b.email || "—"}</div>
                    <div><span className="text-slate-500 inline-block w-32">Source</span>{b.channel || "Direct"}</div>
                  </div>
                  <div className="space-y-1 text-right">
                    <div><b>{fmtDate(data.operating_day || new Date().toISOString().slice(0, 10))}</b><span className="text-slate-500 inline-block w-32 ml-3">Date of Invoice</span></div>
                    <div><b>{data.unit?.name || "—"}</b><span className="text-slate-500 inline-block w-32 ml-3">Room / Unit</span></div>
                    <div><b>{b.adults || 1} Adult{b.adults > 1 ? "s" : ""} / {b.children || 0} Child</b><span className="text-slate-500 inline-block w-32 ml-3">Guests</span></div>
                    <div><b>{b.rate_plan || "Base rate"}</b><span className="text-slate-500 inline-block w-32 ml-3">Rate Type</span></div>
                    <div><b>{nights}</b><span className="text-slate-500 inline-block w-32 ml-3">No. of Nights</span></div>
                    <div><b>{fmtDate(b.checkin)}</b><span className="text-slate-500 inline-block w-32 ml-3">Arrival</span></div>
                    <div><b>{fmtDate(b.checkout)}</b><span className="text-slate-500 inline-block w-32 ml-3">Departure</span></div>
                  </div>
                </div>

                {/* Line-item table with HSN/SAC + SGST/CGST — group-aware */}
                {(() => {
                  // Aggregate values pulled from perUnitInvoice (computed at top level)
                  const grand = groupTotals.grand;
                  const taxable = groupTotals.taxable;
                  const totalTax = groupTotals.tax;
                  const sgst = totalTax / 2;
                  const cgst = totalTax / 2;
                  // Rate label — if every line uses the same tax_rate we can print it
                  // once at the top of the SGST/CGST columns; otherwise fall back to "GST".
                  const rateSet = new Set(perUnitInvoice.map((x) => x.taxRate));
                  const uniformRate = rateSet.size === 1 ? [...rateSet][0] : null;
                  const halfLabel = uniformRate != null
                    ? `${(uniformRate / 2).toFixed(uniformRate % 2 === 0 ? 1 : 2)}%`
                    : "%";
                  return (
                    <>
                      {isGrouped && (
                        <p className="text-[10px] text-slate-500 italic mb-1">
                          Consolidated invoice — {groupBookings.length} units under one reservation for <b>{b.guest || "guest"}</b>. Each unit is billed as a separate line item with its own SGST + CGST split.
                        </p>
                      )}
                      {!isGrouped && b.rate_inclusive && (
                        <p className="text-[10px] text-slate-500 italic mb-1">
                          Rate negotiated <b>inclusive of {perUnitInvoice[0].taxRate}% GST</b> — the "Rate" column shows the taxable per-night value, and SGST + CGST are separated out per statutory format.
                        </p>
                      )}
                      <table className="w-full text-[11px] border border-slate-300 mb-4">
                        <thead>
                          <tr className="bg-slate-100 border-b border-slate-300">
                            <th className="text-left px-2 py-1.5 border-r border-slate-300">Sr</th>
                            <th className="text-left px-2 py-1.5 border-r border-slate-300">Particular</th>
                            <th className="text-left px-2 py-1.5 border-r border-slate-300">HSN/SAC</th>
                            <th className="text-right px-2 py-1.5 border-r border-slate-300">Qty</th>
                            <th className="text-right px-2 py-1.5 border-r border-slate-300">Rate</th>
                            <th className="text-right px-2 py-1.5 border-r border-slate-300">Taxable</th>
                            <th className="text-right px-2 py-1.5 border-r border-slate-300">Tax %</th>
                            <th className="text-right px-2 py-1.5 border-r border-slate-300">SGST</th>
                            <th className="text-right px-2 py-1.5">CGST</th>
                          </tr>
                        </thead>
                        <tbody>
                          {perUnitInvoice.map((x, idx) => {
                            const perNight = x.taxable / (x.gnights || 1);
                            return (
                              <tr key={x.gb.id || idx}>
                                <td className="px-2 py-1.5 border-r border-b border-slate-300">{idx + 1}</td>
                                <td className="px-2 py-1.5 border-r border-b border-slate-300">
                                  {isGrouped ? <>Unit <b>{x.gb.unit_name || "?"}</b> · {fmtDate(x.gb.checkin)}→{fmtDate(x.gb.checkout)}</> : <>Room Charges — {x.gb.rate_plan || "Base rate"}</>}
                                </td>
                                <td className="px-2 py-1.5 border-r border-b border-slate-300">996311</td>
                                <td className="text-right px-2 py-1.5 border-r border-b border-slate-300">{x.gnights}</td>
                                <td className="text-right px-2 py-1.5 border-r border-b border-slate-300">{perNight.toFixed(2)}</td>
                                <td className="text-right px-2 py-1.5 border-r border-b border-slate-300">{x.taxable.toFixed(2)}</td>
                                <td className="text-right px-2 py-1.5 border-r border-b border-slate-300 font-mono">{Number(x.taxRate).toFixed(1)}%</td>
                                <td className="text-right px-2 py-1.5 border-r border-b border-slate-300">{(x.tax / 2).toFixed(2)}</td>
                                <td className="text-right px-2 py-1.5 border-b border-slate-300">{(x.tax / 2).toFixed(2)}</td>
                              </tr>
                            );
                          })}
                          <tr className="bg-slate-50 font-bold">
                            <td colSpan={5} className="text-right px-2 py-1.5 border-r border-slate-300">Total</td>
                            <td className="text-right px-2 py-1.5 border-r border-slate-300">{taxable.toFixed(2)}</td>
                            <td className="text-right px-2 py-1.5 border-r border-slate-300">{uniformRate != null ? `${Number(uniformRate).toFixed(1)}%` : "mixed"}</td>
                            <td className="text-right px-2 py-1.5 border-r border-slate-300">{sgst.toFixed(2)}</td>
                            <td className="text-right px-2 py-1.5">{cgst.toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>

                      {/* Right-aligned totals stack + payment history side-by-side */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-[11px] uppercase font-semibold text-slate-500 mb-1">Payment History</p>
                          {(!b.payments || !b.payments.length) ? (
                            <p className="text-[11px] text-slate-400 italic">No payments recorded</p>
                          ) : (
                            <table className="w-full text-[11px] border border-slate-300">
                              <thead>
                                <tr className="bg-slate-100">
                                  <th className="text-left px-2 py-1 border-r border-b border-slate-300">Date</th>
                                  <th className="text-left px-2 py-1 border-r border-b border-slate-300">Description</th>
                                  <th className="text-right px-2 py-1 border-b border-slate-300">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {b.payments.map((p, i) => (
                                  <tr key={i}>
                                    <td className="px-2 py-1 border-r border-b border-slate-300">{fmtDate((p.at || "").slice(0, 10))}</td>
                                    <td className="px-2 py-1 border-r border-b border-slate-300">{p.mode}{p.remark ? ` — ${p.remark}` : ""}</td>
                                    <td className="text-right px-2 py-1 border-b border-slate-300">{Number(p.amount).toFixed(2)}</td>
                                  </tr>
                                ))}
                                <tr className="bg-slate-50 font-bold">
                                  <td colSpan={2} className="text-right px-2 py-1 border-r border-slate-300">Total</td>
                                  <td className="text-right px-2 py-1">{paid.toFixed(2)}</td>
                                </tr>
                              </tbody>
                            </table>
                          )}
                        </div>
                        <div className="text-[12px] space-y-1">
                          <div className="flex justify-between"><span className="text-slate-500">Total Charges (₹)</span><b>{groupTotals.taxable.toFixed(2)}</b></div>
                          <div className="flex justify-between"><span className="text-slate-500">Total SGST (₹)</span><b>{(groupTotals.tax / 2).toFixed(2)}</b></div>
                          <div className="flex justify-between"><span className="text-slate-500">Total CGST (₹)</span><b>{(groupTotals.tax / 2).toFixed(2)}</b></div>
                          <div className="flex justify-between"><span className="text-slate-500">Total Discount (₹)</span>0.00</div>
                          <div className="flex justify-between border-t-2 border-slate-800 pt-1 mt-2 text-[13px]"><span>Total Payable (₹)</span><b>{groupTotals.grand.toFixed(2)}</b></div>
                          <div className="flex justify-between text-emerald-700"><span>Total Payment Received (₹)</span><b>{paid.toFixed(2)}</b></div>
                          <div className={`flex justify-between text-[14px] font-bold px-2 py-1.5 rounded ${balance > 0 ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-emerald-50 text-emerald-800 border border-emerald-200"}`}><span>{balance > 0 ? "Balance Due (₹)" : "Fully Paid ✓"}</span><span>{balance.toFixed(2)}</span></div>
                        </div>
                      </div>
                    </>
                  );
                })()}

                {/* Footer */}
                <div className="border-t border-slate-300 pt-3 mt-4 grid grid-cols-2 gap-4 text-[10px] text-slate-500">
                  <div>This Folio is in ₹ (INR)</div>
                  <div className="text-right">(Guest Signature) — This is a computer-generated invoice.</div>
                </div>
                </>) : (<VoucherBody data={data} b={b} paid={paid} balance={balance} nights={nights} fmtDMY={fmtDMY} fmtTime12={fmtTime12} groupBookings={groupBookings} groupTotals={groupTotals} isGrouped={isGrouped} perUnitInvoice={perUnitInvoice} />)}
              </div>
              <button data-testid="print-invoice-btn" onClick={() => { setDocKind("invoice"); setTimeout(printInvoice, 60); }} className="flex items-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-full px-5 py-2">
                <Printer size={14} /> Print / Download Tax Invoice
              </button>
              <button data-testid="print-voucher-btn" onClick={() => { setDocKind("voucher"); setTimeout(printInvoice, 60); }} className="ml-2 flex items-center gap-2 bg-white border border-[#4F46E5] text-[#4F46E5] hover:bg-[#EEF2FF] text-sm font-semibold rounded-full px-5 py-2">
                <Printer size={14} /> Print / Download Reservation Voucher
              </button>
              <p className="text-[11px] text-slate-500 mt-2">Both documents pull live from this property's General Settings (logo, GSTIN, address) and reflect the actual payments recorded — Total Payable · Total Payment · Balance Due are always in sync with what you've entered under Payments.</p>
              {!data.profile?.reg_number && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <b>No GSTIN set for this property.</b> Add it in Property Configuration → Profile so it appears on every invoice.
                </p>
              )}
            </div>
          )}

          {tab === "payments" && (
            <div className="space-y-4">
              {isGrouped && (
                <p className="text-[11px] text-slate-500 bg-[#EEF2FF] border border-[#C7D2FE] rounded-lg px-3 py-2">
                  <b>Group reservation</b> — Amount / Paid / Balance are shown across all {groupBookings.length} units. Payments apply to the parent booking's ledger.
                </p>
              )}
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-slate-50 rounded-xl py-3">
                  <p className="text-[10px] uppercase text-slate-400 font-semibold">Amount</p>
                  <p className="font-display text-lg font-bold text-slate-900">{INR(isGrouped ? groupTotals.grand : b.amount)}</p>
                </div>
                <div className="bg-emerald-50 rounded-xl py-3">
                  <p className="text-[10px] uppercase text-emerald-700 font-semibold">Paid</p>
                  <p className="font-display text-lg font-bold text-emerald-700">{INR(paid)}</p>
                </div>
                <div className={`rounded-xl py-3 ${balance > 0 ? "bg-amber-50" : "bg-slate-50"}`}>
                  <p className="text-[10px] uppercase font-semibold" style={{ color: balance > 0 ? "#b45309" : "#64748b" }}>Balance</p>
                  <p className="font-display text-lg font-bold" style={{ color: balance > 0 ? "#b45309" : "#64748b" }}>{INR(balance)}</p>
                </div>
              </div>

              <div className="border border-slate-200 rounded-xl p-4">
                <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-400 mb-2">Record a payment</p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <input data-testid="payment-amount" type="number" min="0" step="1" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} placeholder="Amount ₹" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                  <select data-testid="payment-mode" value={pay.mode} onChange={(e) => setPay({ ...pay, mode: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
                    <option>UPI</option><option>Card</option><option>Cash</option><option>Bank transfer</option><option>Razorpay</option>
                  </select>
                  <input data-testid="payment-remark" value={pay.remark} onChange={(e) => setPay({ ...pay, remark: e.target.value })} placeholder="Remark (optional)" className="border border-slate-300 rounded-lg px-3 py-2 text-sm md:col-span-1" />
                  <button data-testid="add-payment-btn" onClick={addPayment} disabled={busy || !pay.amount}
                    className="bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg px-4 py-2 disabled:opacity-50">
                    Add payment
                  </button>
                </div>
              </div>

              <div>
                <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-400 mb-2">History</p>
                {(!b.payments || !b.payments.length) ? (
                  <p className="text-xs text-slate-400 py-4 text-center bg-slate-50 rounded-xl">No payments yet.</p>
                ) : (
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                        <tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Mode</th><th className="text-left px-3 py-2">Remark</th><th className="text-right px-3 py-2">Amount</th></tr>
                      </thead>
                      <tbody>
                        {b.payments.map((p, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-3 py-2 text-xs">{fmtDate((p.at || "").slice(0, 10))}</td>
                            <td className="px-3 py-2 text-xs">{p.mode}</td>
                            <td className="px-3 py-2 text-xs text-slate-500">{p.remark || "—"}</td>
                            <td className="px-3 py-2 text-xs text-right font-semibold">{INR(p.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Add-Unit mini-picker overlay (rendered as a nested modal) ── */}
      {addUnit.open && (
        <div data-testid="add-unit-dialog" className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4" onClick={() => setAddUnit({ ...addUnit, open: false })}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
              <span className="h-8 w-8 rounded-md bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center"><Plus size={14} /></span>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold text-slate-900 text-sm">Add unit to this reservation</h3>
                <p className="text-[10px] text-slate-500">Adds another unit for <b>{b.guest}</b> under the same consolidated invoice.</p>
              </div>
              <button data-testid="add-unit-close" onClick={() => setAddUnit({ ...addUnit, open: false })} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3 flex-1 overflow-y-auto">
              <div>
                <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Units available for {addUnit.checkin || "…"} → {addUnit.checkout || "…"}</label>
                {addUnit.loadingAvail ? (
                  <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-400 py-3 justify-center"><Loader2 className="animate-spin" size={13} /> Checking availability &amp; blocking groups…</div>
                ) : addUnit.available.length === 0 ? (
                  <p className="mt-1 text-[11px] text-slate-400 py-3 text-center bg-slate-50 rounded-lg">No units are free for these dates. Change the check-in/out above to search again.</p>
                ) : (
                  <div data-testid="add-unit-picker" className="mt-1 max-h-40 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {addUnit.available.map((u) => {
                      const picked = addUnit.unit_ids.includes(u.id);
                      return (
                        <label key={u.id} data-testid={`add-unit-opt-${u.id}`}
                          className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${picked ? "bg-indigo-50" : "hover:bg-slate-50"}`}>
                          <input type="checkbox" checked={picked} onChange={() => toggleAddUnitId(u.id)} className="accent-[#4F46E5]" />
                          Unit {u.name}
                          {picked && <span className="ml-auto text-[10px] text-[#4F46E5] font-semibold">selected</span>}
                        </label>
                      );
                    })}
                  </div>
                )}
                <p className="text-[10px] text-slate-400 mt-1">Only units with no overlapping booking on themselves or their blocking-group siblings are shown.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Check-in</label>
                  <input data-testid="add-unit-checkin" type="date" value={addUnit.checkin}
                    onChange={(e) => setAddUnit({ ...addUnit, checkin: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Check-out</label>
                  <input data-testid="add-unit-checkout" type="date" value={addUnit.checkout}
                    onChange={(e) => setAddUnit({ ...addUnit, checkout: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Rate / night ₹ <span className="text-slate-300 normal-case">{addUnit.rate_inclusive ? "(incl.)" : "(excl.)"}</span></label>
                  <input data-testid="add-unit-rate" inputMode="decimal" value={addUnit.rate}
                    onChange={(e) => setAddUnit({ ...addUnit, rate: e.target.value.replace(/[^\d.]/g, "") })}
                    placeholder="0" className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono text-right" />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Adult</label>
                  <input type="number" min="1" value={addUnit.adults}
                    onChange={(e) => setAddUnit({ ...addUnit, adults: parseInt(e.target.value || "1", 10) })}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono text-right" />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Child</label>
                  <input type="number" min="0" value={addUnit.children}
                    onChange={(e) => setAddUnit({ ...addUnit, children: parseInt(e.target.value || "0", 10) })}
                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono text-right" />
                </div>
              </div>
              <div className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-[11px]">
                <span className="text-slate-500">Rate mode</span>
                <div className="inline-flex bg-white rounded-full p-0.5 border border-slate-200">
                  <button data-testid="add-unit-incl" onClick={() => setAddUnit({ ...addUnit, rate_inclusive: true })}
                    className={`px-3 py-1 rounded-full text-[10px] font-semibold transition-colors ${addUnit.rate_inclusive ? "bg-slate-900 text-white" : "text-slate-500"}`}>Incl. taxes</button>
                  <button data-testid="add-unit-excl" onClick={() => setAddUnit({ ...addUnit, rate_inclusive: false })}
                    className={`px-3 py-1 rounded-full text-[10px] font-semibold transition-colors ${!addUnit.rate_inclusive ? "bg-slate-900 text-white" : "text-slate-500"}`}>Excl. taxes</button>
                </div>
              </div>
              <div className="flex items-center justify-between text-[11px] text-slate-500 bg-[#EEF2FF] rounded-lg px-3 py-2">
                <span>Tax rate (%)</span>
                <input type="number" min="0" max="30" value={addUnit.tax_rate}
                  onChange={(e) => setAddUnit({ ...addUnit, tax_rate: parseFloat(e.target.value || "0") })}
                  className="w-16 border border-slate-200 rounded px-2 py-0.5 text-xs font-mono text-right bg-white" />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-2">
              <button data-testid="add-unit-cancel" onClick={() => setAddUnit({ ...addUnit, open: false })}
                className="px-4 py-2 text-sm font-semibold text-slate-600 rounded-full hover:bg-slate-100">Cancel</button>
              <button data-testid="add-unit-submit" onClick={submitAddUnit} disabled={busy || !addUnit.unit_ids.length || !addUnit.rate}
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-full px-5 py-2 disabled:opacity-50 flex items-center gap-2">
                {busy ? <Loader2 className="animate-spin" size={13} /> : <Plus size={13} />} Add {addUnit.unit_ids.length > 1 ? `${addUnit.unit_ids.length} units` : "to reservation"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Mandatory ID verification at check-in ── */}
      {idVerify.open && (
        <div data-testid="id-verify-dialog" className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4" onClick={() => setIdVerify({ ...idVerify, open: false })}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
              <span className="h-8 w-8 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center"><LogIn size={14} /></span>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold text-slate-900 text-sm">ID verification · check-in</h3>
                <p className="text-[10px] text-slate-500">Compliance — all three fields are required before check-in completes for <b>{b.guest}</b>.</p>
              </div>
              <button onClick={() => setIdVerify({ ...idVerify, open: false })} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3 flex-1 overflow-y-auto">
              <div>
                <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">ID type *</label>
                <select data-testid="idv-type" value={idVerify.id_type} onChange={(e) => setIdVerify({ ...idVerify, id_type: e.target.value })}
                  className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                  {["Aadhaar", "Passport", "Driver's License", "Voter ID", "PAN", "Other"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">ID number *</label>
                <input data-testid="idv-number" value={idVerify.id_number} onChange={(e) => setIdVerify({ ...idVerify, id_number: e.target.value })}
                  placeholder="Enter the ID number exactly as printed" className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono uppercase" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">ID photo(s) * — up to 4</label>
                <input
                  data-testid="idv-photo-file"
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    if (!files.length) return;
                    const room = 4 - (idVerify.id_photos?.length || 0);
                    if (room <= 0) return toast.error("Maximum 4 IDs already uploaded — remove one first.");
                    const take = files.slice(0, room);
                    if (take.length < files.length) toast.warning(`Only the first ${take.length} file(s) uploaded — 4-ID limit.`);
                    setBusy(true);
                    const newPaths = [];
                    for (const f of take) {
                      try {
                        const fd = new FormData(); fd.append("file", f);
                        const { data: r } = await api.post("/pms/uploads/id-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                        newPaths.push(r.path);
                      } catch (err) { toast.error(errMsg(err)); }
                    }
                    setIdVerify((prev) => ({ ...prev,
                      id_photos: [...(prev.id_photos || []), ...newPaths].slice(0, 4),
                      id_photo_url: prev.id_photo_url || newPaths[0] || ""
                    }));
                    setBusy(false);
                    if (newPaths.length) toast.success(`${newPaths.length} ID photo(s) uploaded`);
                    e.target.value = "";
                  }}
                  className="mt-1 w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-slate-900 file:text-white file:text-[11px] file:font-semibold"
                />
                {(idVerify.id_photos || []).length > 0 && (
                  <div className="mt-2 space-y-1">
                    {(idVerify.id_photos || []).map((p, idx) => (
                      <div key={idx} data-testid={`idv-photo-item-${idx}`} className="flex items-center gap-2 text-[10px] text-slate-600 font-mono bg-slate-50 border border-slate-200 rounded px-2 py-1">
                        <span className="text-emerald-600">✓</span>
                        <span className="truncate flex-1">{p.split("/").pop()}</span>
                        <button type="button" onClick={() => setIdVerify((prev) => ({ ...prev, id_photos: prev.id_photos.filter((_, i) => i !== idx) }))}
                          className="text-slate-400 hover:text-red-500">Remove</button>
                      </div>
                    ))}
                    <p className="text-[10px] text-slate-400">{(idVerify.id_photos || []).length}/4 uploaded</p>
                  </div>
                )}
                <p className="text-[10px] text-slate-400 mt-1">JPG / PNG / PDF · max 8 MB each · up to 4 IDs (covers up to 4 guests per booking) · stored securely in platform object storage.</p>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-2">
              <button onClick={() => setIdVerify({ ...idVerify, open: false })}
                className="px-4 py-2 text-sm font-semibold text-slate-600 rounded-full hover:bg-slate-100">Cancel</button>
              <button data-testid="idv-submit" onClick={submitIdVerify}
                disabled={busy || !idVerify.id_type || !idVerify.id_number || ((idVerify.id_photos || []).length === 0 && !idVerify.id_photo_url)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-full px-5 py-2 disabled:opacity-50 flex items-center gap-2">
                {busy ? <Loader2 className="animate-spin" size={13} /> : <LogIn size={13} />} Verify &amp; check in
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Housekeeping prompt right after check-in ── */}
      {hkPrompt && (
        <div data-testid="hk-prompt-dialog" className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4" onClick={() => setHkPrompt(false)}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4">
              <h3 className="font-display font-bold text-slate-900 text-sm">Is Unit {data.unit?.name} clean?</h3>
              <p className="text-[11px] text-slate-500 mt-1">Yes will mark the unit as <b>Clean</b> on the Housekeeping board and clear any open dirty task.</p>
            </div>
            <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-2">
              <button data-testid="hk-prompt-no" onClick={() => setHkPrompt(false)}
                className="px-4 py-2 text-sm font-semibold text-slate-600 rounded-full hover:bg-slate-100">No, leave dirty</button>
              <button data-testid="hk-prompt-yes" onClick={markUnitClean} disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-full px-5 py-2 disabled:opacity-50">
                Yes · mark clean
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </Portal>
  );
}

/* ─────────────────────────────────────────────────────────────
   Reservation Voucher — Atiti Homestays style (from Yanolja ref)
   Guest-facing, ready to print/email. Pulls live from the
   property's General Settings and Policies. If a property hasn't
   set its own cancellation / hotel policy, we render the Atiti
   defaults inline so the voucher is never blank.
   ───────────────────────────────────────────────────────────── */
const ATITI_CANCELLATION_POLICY = "Free cancellation within 48 hrs of booking | 50% refund up to 7 days before check-in | No refund within 7 days of stay | Date change allowed once (subject to availability) | Early checkout non-refundable | No-show treated as cancellation | Booking transferable on request | Platform-specific policies (Airbnb/Booking.com) override | Refunds processed within 7 working days | Fair, transparent & guest-friendly — the Atiti Promise.";
const ATITI_HOTEL_POLICY = "Check-in 11 AM | Check-out 10 AM | Valid ID mandatory | No parties or loud music | No smoking indoors | Respect neighbors | Maintain cleanliness | Damage chargeable | Cancellation as per booking terms | Pets with prior approval | Guest limit as per booking | Kitchen use allowed with care | Loss/damage to property recoverable | Management rights reserved | Stay with comfort, care & courtesy — the Atiti Way.";

function VoucherBody({ data, b, paid, balance, nights, fmtDMY, fmtTime12, groupBookings, groupTotals, isGrouped, perUnitInvoice = [] }) {
  const p = data.property || {};
  const prof = data.profile || {};
  const pol = prof.policies || {};
  const unitName = data.unit?.name || "—";
  const addr = [prof.address_1, prof.address_2, prof.city, prof.state, prof.postal_code, prof.country].filter(Boolean).join(", ");
  const guests = `${b.adults || 1} adult${(b.adults || 1) > 1 ? "s" : ""} & ${b.children || 0} child${(b.children || 0) === 1 ? "" : "ren"}`;
  const bookingRef = (b.id || "").slice(0, 8).toUpperCase();
  // ── Grouped totals take precedence over single-booking math ──
  const groupList = groupBookings && groupBookings.length ? groupBookings : [b];
  const grand = isGrouped ? Number(groupTotals?.grand || 0) : Number(b.amount || 0);
  const savedRate = (typeof b.tax_rate === "number" && !b.tax_exempt) ? b.tax_rate : (b.tax_exempt ? 0 : 5);
  const gstRate = savedRate / 100;
  const roomCharges = isGrouped
    ? Number(groupTotals?.taxable || 0)
    : (gstRate > 0 ? Number(b.amount || 0) / (1 + gstRate) : Number(b.amount || 0));
  const roomTax = isGrouped
    ? Number(groupTotals?.tax || 0)
    : Math.max(0, Number(b.amount || 0) - roomCharges);
  const bookingDate = fmtDMY((b.created_at || new Date().toISOString()).slice(0, 10));
  const arrivalStamp = fmtTime12(b.ci_time || "11:00");
  const checkoutStamp = `${fmtDMY(b.checkout)} ${fmtTime12(b.co_time || "10:00")}`;
  const billedName = b.company_name || b.guest || "Guest";
  return (
    <div className="voucher-doc text-[11px] leading-relaxed">
      {/* ── Header ── */}
      <div className="flex items-start justify-between border-b-2 border-[#111827] pb-4 mb-5">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {prof.logo_url ? (
            <img src={prof.logo_url} alt="logo" className="h-16 w-16 object-contain" />
          ) : (
            <div className="h-16 w-16 rounded-lg bg-[#EC4899]/10 border border-[#EC4899]/30 flex items-center justify-center text-[#EC4899] font-black text-2xl italic">A</div>
          )}
          <div className="min-w-0">
            <h2 className="font-black text-[15px] leading-tight text-slate-900">{p.name || "Property"}</h2>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Powered by Atiti Homestays Private Limited</p>
          </div>
        </div>
        <div className="text-right text-[10px] text-slate-700 space-y-0.5 max-w-[45%]">
          {prof.reg_number && <p>GSTIN: <b className="font-mono">{prof.reg_number}</b></p>}
          {addr && <p>{addr}</p>}
          {prof.email && <p>{prof.email}</p>}
          {prof.phone && <p>Phone: {prof.phone}</p>}
          {prof.website && <p>{prof.website}</p>}
        </div>
      </div>

      {/* ── Confirm Booking banner ── */}
      <div className="mb-5">
        <p className="text-[11px] font-bold text-slate-900 tracking-wider">CONFIRM BOOKING</p>
        <p className="text-[18px] font-black text-slate-900 leading-tight mt-1">BOOKING REFERENCE NO : <span className="font-mono">{bookingRef}</span></p>
        <p className="text-[10px] text-slate-500 mt-1 italic">Kindly print this confirmation and have it ready upon check-in at the Hotel</p>
      </div>

      {/* ── Greeting ── */}
      <p className="mb-1"><b>Dear {b.guest || "Guest"},</b></p>
      <p className="mb-5 text-slate-700">Thank you for choosing <b>{p.name || "our property"}</b> Powered by Atiti Homestays Private Limited for your stay. We are pleased to inform you that your reservation request is <b className="text-emerald-700">CONFIRMED</b> and your reservation details are as follows.</p>

      {/* ── Your Details + Booking Details ── */}
      <div className="grid grid-cols-2 gap-8 mb-5">
        <div>
          <p className="font-bold text-slate-900 mb-2 border-b border-slate-200 pb-1">Your Details</p>
          <div className="space-y-1">
            <div><b>{b.guest || "—"}</b>{b.nationality ? `, ${b.nationality}` : ""}</div>
            <div>Email ID : {b.email || ""}</div>
            {b.mobile && <div>Phone : {b.mobile}</div>}
            {b.address && <div>Address : {b.address}</div>}
            {b.tax_id && <div>Tax ID / GSTIN : <b className="font-mono uppercase">{b.tax_id}</b></div>}
            {b.company_name && b.bill_to !== "Guest" && (
              <div className="mt-1 text-slate-600">Billed to <b>{b.company_name}</b> ({b.bill_to})</div>
            )}
          </div>
        </div>
        <div>
          <p className="font-bold text-slate-900 mb-2 border-b border-slate-200 pb-1">Booking Details</p>
          <div className="space-y-1">
            <div>Booking Date : <b>{bookingDate}</b></div>
            <div>Check In Date : <b>{fmtDMY(b.checkin)} {fmtTime12(b.ci_time || pol.checkin_time || "11:00")}</b></div>
            <div>Check Out Date : <b>{checkoutStamp}</b></div>
            <div>Nights : <b>{nights}</b></div>
            <div>Arrival Time : <b>{arrivalStamp}</b></div>
            <div>Special Request : {b.special_request || ""}</div>
          </div>
        </div>
      </div>

      {/* ── Rooms Details table — one row per unit in a group ── */}
      <p className="font-bold text-slate-900 mb-2 border-b border-slate-200 pb-1">Rooms Details {isGrouped && <span className="text-[10px] text-slate-500 font-normal">· {groupList.length} units on this reservation</span>}</p>
      <table className="w-full text-[10px] border border-slate-300 mb-1">
        <thead>
          <tr className="bg-slate-50">
            <th className="text-left px-2 py-1.5 border-r border-slate-300">Room Type</th>
            <th className="text-left px-2 py-1.5 border-r border-slate-300">Check-in → Check-out</th>
            <th className="text-left px-2 py-1.5 border-r border-slate-300">Nights</th>
            <th className="text-left px-2 py-1.5 border-r border-slate-300">Guest(s)</th>
            <th className="text-right px-2 py-1.5">Line total (Rs)</th>
          </tr>
        </thead>
        <tbody>
          {groupList.map((gb, idx) => {
            const gname = gb.unit_name || unitName;
            const gnights = gb.nights || Math.max(1, Math.round((new Date(gb.checkout) - new Date(gb.checkin)) / 86400000));
            const gguests = `${gb.adults || 1}A + ${gb.children || 0}C`;
            return (
              <tr key={gb.id || idx}>
                <td className="px-2 py-1.5 border-r border-t border-slate-300">Unit <b>{gname}</b> · {gb.rate_plan || "EP"}</td>
                <td className="px-2 py-1.5 border-r border-t border-slate-300">{fmtDMY(gb.checkin)} → {fmtDMY(gb.checkout)}</td>
                <td className="px-2 py-1.5 border-r border-t border-slate-300">{gnights}</td>
                <td className="px-2 py-1.5 border-r border-t border-slate-300">{gguests}</td>
                <td className="text-right px-2 py-1.5 border-t border-slate-300 font-mono">{Number(gb.amount || 0).toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* ── Rates Details table ── */}
      <p className="font-bold text-slate-900 mt-4 mb-2 border-b border-slate-200 pb-1">Rates Details</p>
      <table className="w-full text-[10px] border border-slate-300 mb-4">
        <thead>
          <tr className="bg-slate-50">
            <th className="text-left px-2 py-1.5 border-r border-slate-300">Details</th>
            <th className="text-right px-2 py-1.5">Rates (Rs)</th>
          </tr>
        </thead>
        <tbody>
          <tr><td className="px-2 py-1.5 border-r border-t border-slate-300">Total Room Charges</td><td className="text-right px-2 py-1.5 border-t border-slate-300 font-mono">{roomCharges.toFixed(2)}</td></tr>
          {/* Batch-2 Item 2: when the reservation spans multiple units with
              possibly-different tax slabs, show one Tax row per unit; else a
              single "Tax" row with the (uniform) rate. */}
          {isGrouped && perUnitInvoice.length > 1 ? (
            <>
              {perUnitInvoice.map((x, i) => (
                <tr key={`tax-${x.gb.id || i}`}>
                  <td className="px-2 py-1.5 border-r border-t border-slate-300">
                    Tax — Unit <b>{x.gb.unit_name || "?"}</b> ({Number(x.taxRate).toFixed(1)}%)
                  </td>
                  <td className="text-right px-2 py-1.5 border-t border-slate-300 font-mono">{Number(x.tax).toFixed(2)}</td>
                </tr>
              ))}
              <tr className="text-slate-500">
                <td className="px-2 py-1.5 border-r border-t border-slate-300 italic">Tax total</td>
                <td className="text-right px-2 py-1.5 border-t border-slate-300 font-mono italic">{roomTax.toFixed(2)}</td>
              </tr>
            </>
          ) : (
            <tr>
              <td className="px-2 py-1.5 border-r border-t border-slate-300">Tax {perUnitInvoice[0]?.taxRate ? `(${Number(perUnitInvoice[0].taxRate).toFixed(1)}%)` : ""}</td>
              <td className="text-right px-2 py-1.5 border-t border-slate-300 font-mono">{roomTax.toFixed(2)}</td>
            </tr>
          )}
          <tr><td className="px-2 py-1.5 border-r border-t border-slate-300">Inclusions Including Tax</td><td className="text-right px-2 py-1.5 border-t border-slate-300 font-mono">0.00</td></tr>
          <tr><td className="px-2 py-1.5 border-r border-t border-slate-300">Extra Charges Including Discount and Tax</td><td className="text-right px-2 py-1.5 border-t border-slate-300 font-mono">0.00</td></tr>
          <tr><td className="px-2 py-1.5 border-r border-t border-slate-300">Round Off</td><td className="text-right px-2 py-1.5 border-t border-slate-300 font-mono">0.00</td></tr>
          <tr className="bg-slate-50 font-bold"><td className="px-2 py-1.5 border-r border-t border-slate-300">Grand Total</td><td className="text-right px-2 py-1.5 border-t border-slate-300 font-mono">{grand.toFixed(2)}</td></tr>
          <tr className="font-bold text-emerald-700"><td className="px-2 py-1.5 border-r border-t border-slate-300">Total Paid</td><td className="text-right px-2 py-1.5 border-t border-slate-300 font-mono">{paid.toFixed(2)}</td></tr>
          <tr className={`font-bold ${balance > 0 ? "text-amber-800 bg-amber-50" : "text-emerald-700 bg-emerald-50"}`}>
            <td className="px-2 py-1.5 border-r border-t border-slate-300">Amount due at time of check in</td>
            <td className="text-right px-2 py-1.5 border-t border-slate-300 font-mono">{balance.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>

      {/* ── Page 2 — Conditions & Policies ── */}
      <div className="voucher-page-break" style={{ pageBreakBefore: "always" }} />
      <div className="grid grid-cols-2 gap-6 mb-5 mt-2">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">BOOKING AMOUNT</p>
          <p className="text-[22px] font-black text-slate-900">Rs {grand.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-[12px] font-semibold text-slate-500">(INR)</span></p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Booked & Payable By</p>
          <p className="text-[13px] font-bold text-slate-900 mt-1">{billedName}</p>
          <p className="text-[11px] text-slate-600">{b.nationality || prof.country || "India"}</p>
        </div>
      </div>

      <h3 className="text-[16px] font-black text-slate-900 mb-3">Conditions &amp; Policies</h3>

      <p className="font-bold text-slate-900 mt-3">Cancellation Policy</p>
      <p className="text-slate-700 mt-1">Atiti Cancellation Policy: {pol.cancellation || ATITI_CANCELLATION_POLICY}</p>

      <p className="font-bold text-slate-900 mt-4">Hotel Policy</p>
      <p className="text-slate-700 mt-1">Atiti Homestay Policy: {pol.hotel || ATITI_HOTEL_POLICY}</p>

      {pol.house_rules && (
        <>
          <p className="font-bold text-slate-900 mt-4">House Rules</p>
          <p className="text-slate-700 mt-1 whitespace-pre-line">{pol.house_rules}</p>
        </>
      )}

      {pol.terms && (
        <>
          <p className="font-bold text-slate-900 mt-4">Terms &amp; Conditions</p>
          <p className="text-slate-700 mt-1 whitespace-pre-line">{pol.terms}</p>
        </>
      )}

      <p className="font-bold text-slate-900 mt-5 text-[13px]">Cancellation &amp; Refund Policy</p>

      <p className="font-bold text-slate-900 mt-3">1. Cancellation Before Check-in</p>
      <ul className="list-disc pl-5 text-slate-700 space-y-0.5 mt-1">
        <li>Guests may cancel before check-in subject to the cancellation policy selected by the host.</li>
        <li>Refund eligibility, if any, will be processed according to the applicable cancellation terms.</li>
      </ul>

      <p className="font-bold text-slate-900 mt-3">2. Cancellation After Check-in</p>
      <ul className="list-disc pl-5 text-slate-700 space-y-0.5 mt-1">
        <li>Once a guest has checked in, the booking is considered active.</li>
        <li>No automatic refund will be issued for voluntary early check-out.</li>
        <li>Refunds, if any, are solely at the discretion of the host or Atiti under exceptional circumstances.</li>
      </ul>

      <p className="font-bold text-slate-900 mt-3">3. Long-Term Bookings (30 Days or More)</p>
      <ul className="list-disc pl-5 text-slate-700 space-y-0.5 mt-1">
        <li>Advance payments for 1-month, 2-month, 3-month, or longer stays are intended to reserve the accommodation exclusively for the guest.</li>
        <li>If the guest vacates the property before the scheduled check-out date, the unused portion of the booking is generally non-refundable.</li>
        <li>Any exception or partial refund shall be at the sole discretion of the host or Atiti and may be considered only if the remaining stay is successfully re-booked or under exceptional circumstances.</li>
      </ul>

      <p className="font-bold text-slate-900 mt-3">4. No-Show</p>
      <p className="text-slate-700 mt-1">Failure to check in without prior notice will be treated as a No-Show, and the booking amount will be non-refundable.</p>

      <p className="font-bold text-slate-900 mt-3">5. Host Cancellation</p>
      <p className="text-slate-700 mt-1">If a host cancels a confirmed booking without a valid reason, the guest will receive a full refund, and the host may be subject to penalties under Atiti's Host Policy.</p>

      <p className="font-bold text-slate-900 mt-3">6. Exceptional Circumstances</p>
      <p className="text-slate-700 mt-1">Atiti may review refund requests in cases such as:</p>
      <ul className="list-disc pl-5 text-slate-700 space-y-0.5 mt-1">
        <li>Medical emergencies</li>
        <li>Natural disasters</li>
        <li>Government travel restrictions</li>
        <li>Property becoming uninhabitable</li>
        <li>Other force majeure events</li>
      </ul>
      <p className="text-slate-500 italic mt-1">Supporting documentation may be required.</p>

      <p className="font-bold text-slate-900 mt-3">7. Security Deposit</p>
      <p className="text-slate-700 mt-1">Security deposits, where applicable, will be refunded after inspection of the property, subject to deductions for damages, unpaid dues, or violations of house rules.</p>

      <p className="font-bold text-slate-900 mt-3">8. Final Decision</p>
      <p className="text-slate-700 mt-1">Atiti Homestays Private Limited reserves the right to review all disputes and make a fair and final decision regarding cancellations, refunds, and booking-related issues in accordance with this policy.</p>

      <p className="italic text-slate-600 mt-4">By confirming a booking through Atiti, guests acknowledge that they have read, understood, and agreed to this Cancellation &amp; Refund Policy.</p>

      <div className="mt-5 pt-3 border-t border-slate-200 space-y-0.5">
        <p>Hotel Check in Time : <b>{fmtTime12(pol.checkin_time || "11:00")}</b></p>
        <p>Hotel Check out Time : <b>{fmtTime12(pol.checkout_time || "10:00")}</b></p>
      </div>

      <p className="text-[10px] text-slate-500 mt-4 italic">This email has been sent from an automated system — please do not reply to it.</p>

      <div className="mt-4 border-t border-slate-200 pt-3">
        <p className="font-bold text-slate-900 text-center tracking-widest">**** FOR ANY FURTHER QUERY ****</p>
        {prof.email && <p className="text-center mt-1">Contact us by Email Id <b>{prof.email}</b></p>}
        {prof.phone && <p className="text-center">Phone No : <b>{prof.phone}</b></p>}
        {prof.website && <p className="text-center">Website : <b>{prof.website}</b></p>}
        {prof.reg_number && <p className="text-center">GSTIN : <b className="font-mono">{prof.reg_number}</b></p>}
        {addr && <p className="text-center text-slate-600 mt-1">{addr}</p>}
      </div>
    </div>
  );
}
